import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, InputPassword, Select } from '../../../components/ui';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import {
  LOAI_VAN_BAN,
  NGUON_TICH_HOP,
  NHAN_NGUON,
  NHAN_SU_KIEN,
  SU_KIEN_WEBHOOK,
  laNguonBiet,
} from '../constants';
import type {
  DinhDangDauVao,
  GhiTichHop,
  PhuongThucWebhook,
  SuKienWebhook,
  TichHop,
  XacThucDauVao,
  XacThucWebhook,
} from '../types';
import { LoiForm } from './LoiForm';

interface FormState {
  name: string;
  source_label: string;
  default_doc_type: string;
  auto_summarize: boolean;
  inbound_auth_type: XacThucDauVao;
  inbound_format: DinhDangDauVao;
  webhook_url: string;
  webhook_method: PhuongThucWebhook;
  webhook_auth_type: XacThucWebhook;
  webhook_token: string;
  webhook_triggers: SuKienWebhook[];
  webhook_retry_max: string;
  is_active: boolean;
}

/** Mặc định của form tạo mới, như `EMPTY_FORM` của `useCauHinhDauVao` bản cũ. */
const RONG: FormState = {
  name: '',
  source_label: 'QLVB',
  default_doc_type: LOAI_VAN_BAN[0]!,
  auto_summarize: true,
  inbound_auth_type: 'API_KEY',
  inbound_format: 'MULTIPART',
  webhook_url: '',
  webhook_method: 'POST',
  webhook_auth_type: 'BEARER',
  webhook_token: '',
  webhook_triggers: ['AFTER_SUMMARIZE', 'ON_ERROR'],
  webhook_retry_max: '3',
  is_active: true,
};

type Loi = Partial<Record<'name' | 'webhook_url' | 'webhook_retry_max' | 'webhook_token', string>>;

function Phan({ tieuDe, children }: { tieuDe: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3 rounded-lg border border-default bg-subtle p-3.5">
      <legend className="px-1 text-2xs font-semibold uppercase tracking-wide text-content-secondary">{tieuDe}</legend>
      {children}
    </fieldset>
  );
}

/** Thêm / sửa cấu hình tích hợp (`FormDialog` của `CauHinhDauVaoView` bản cũ). */
export function TichHopFormDialog({
  open,
  tichHop,
  saving,
  loiMayChu,
  onClose,
  onSubmit,
}: {
  open: boolean;
  tichHop: TichHop | null;
  saving: boolean;
  loiMayChu: unknown;
  onClose: () => void;
  onSubmit: (vao: GhiTichHop) => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<FormState>(RONG);
  const [loi, setLoi] = useState<Loi>({});

  useEffect(() => {
    if (!open) return;
    setLoi({});
    setF(
      tichHop
        ? {
            name: tichHop.name,
            source_label: tichHop.source_label,
            default_doc_type: tichHop.default_doc_type ?? '',
            auto_summarize: tichHop.auto_summarize,
            inbound_auth_type: tichHop.inbound_auth_type,
            inbound_format: tichHop.inbound_format,
            webhook_url: tichHop.webhook_url ?? '',
            webhook_method: tichHop.webhook_method,
            webhook_auth_type: tichHop.webhook_auth_type,
            webhook_token: '',
            webhook_triggers: [...tichHop.webhook_triggers],
            webhook_retry_max: String(tichHop.webhook_retry_max),
            is_active: tichHop.is_active,
          }
        : RONG,
    );
  }, [open, tichHop]);

  useEffect(() => {
    const m = loiTheoTruong(loiMayChu);
    if (Object.keys(m).length) {
      setLoi({ name: m['/name'], webhook_url: m['/webhook_url'], webhook_retry_max: m['/webhook_retry_max'] });
    }
  }, [loiMayChu]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));
  const batTat = (s: SuKienWebhook) =>
    setF((p) => ({
      ...p,
      webhook_triggers: p.webhook_triggers.includes(s)
        ? p.webhook_triggers.filter((x) => x !== s)
        : [...p.webhook_triggers, s],
    }));

  const luu = () => {
    const next: Loi = {};
    const name = f.name.trim();
    if (!name) next.name = t('vanBanTichHopForm.loiTenTrong');
    const retry = Number(f.webhook_retry_max);
    if (f.webhook_retry_max.trim() === '' || Number.isNaN(retry) || retry < 0 || retry > 10 || !Number.isInteger(retry)) {
      next.webhook_retry_max = t('vanBanTichHopForm.loiThuLaiSai');
    }
    const url = f.webhook_url.trim();
    if (url && !/^https?:\/\/\S+$/i.test(url)) next.webhook_url = t('vanBanTichHopForm.loiUrlSai');
    // Webhook có xác thực thì cần token. Khi sửa, token cũ đã lưu nên bỏ trống là giữ nguyên;
    // chỉ bắt buộc khi tạo mới hoặc webhook trước đó chưa có xác thực.
    if (url && f.webhook_auth_type !== 'NONE' && !f.webhook_token.trim()) {
      const giuTokenCu = !!tichHop && !!tichHop.webhook_url && tichHop.webhook_auth_type !== 'NONE';
      if (!giuTokenCu) next.webhook_token = t('vanBanTichHopForm.loiTokenTrong');
    }
    setLoi(next);
    if (Object.keys(next).length) return;
    onSubmit({
      name,
      source_label: f.source_label,
      default_doc_type: f.default_doc_type || null,
      auto_summarize: f.auto_summarize,
      inbound_auth_type: f.inbound_auth_type,
      inbound_format: f.inbound_format,
      webhook_url: url || null,
      webhook_method: f.webhook_method,
      webhook_auth_type: f.webhook_auth_type,
      webhook_triggers: f.webhook_triggers,
      webhook_retry_max: retry,
      // Chỉ gửi token khi người dùng nhập mới; bỏ trống là giữ token cũ
      ...(f.webhook_token.trim() ? { webhook_token: f.webhook_token.trim() } : {}),
      ...(tichHop ? { is_active: f.is_active } : {}),
    });
  };

  const nguonOptions = [
    ...NGUON_TICH_HOP.map((v) => ({ value: v, label: t(NHAN_NGUON[v]) })),
    // Nhãn tự do của dữ liệu cũ vẫn chọn được, không bị xoá khi lưu
    ...(laNguonBiet(f.source_label) ? [] : [{ value: f.source_label, label: f.source_label }]),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={620}
      destroyOnHidden
      title={t(tichHop ? 'vanBanTichHopForm.dialogSua' : 'vanBanTichHopForm.dialogThem')}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" icon={<CheckCircleOutlined />} loading={saving} onClick={luu}>
            {t(saving ? 'chung.dangLuu' : tichHop ? 'vanBanChung.luuThayDoi' : 'vanBanTichHopForm.nutTao')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <LoiForm loi={loiMayChu} />
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label={t('vanBanTichHopForm.fTen')} required error={loi.name}>
            {(p) => (
              <Input {...p} placeholder={t('vanBanTichHopForm.fTenGoiY')} value={f.name} onChange={(e) => set('name', e.target.value)} />
            )}
          </Field>
          <Field label={t('vanBanTichHopForm.fNguon')}>
            {(p) => (
              <Select {...p} className="w-full" options={nguonOptions} value={f.source_label} onChange={(v) => set('source_label', v as string)} />
            )}
          </Field>
          <Field label={t('vanBanTichHopForm.fLoaiVb')}>
            {(p) => (
              <Select
                {...p}
                className="w-full"
                options={LOAI_VAN_BAN.map((x) => ({ value: x, label: x }))}
                value={f.default_doc_type || undefined}
                onChange={(v) => set('default_doc_type', (v as string | undefined) ?? '')}
              />
            )}
          </Field>
          <Field label={t('vanBanTichHopForm.fTuDong')}>
            {() => (
              <div className="flex h-8 items-center">
                <Checkbox checked={f.auto_summarize} onChange={(e) => set('auto_summarize', e.target.checked)}>
                  <span className="text-sm text-content-secondary">{t('vanBanTichHopForm.fTuDongMo')}</span>
                </Checkbox>
              </div>
            )}
          </Field>
        </div>

        <Phan tieuDe={t('vanBanTichHopForm.phanTiepNhan')}>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <Field label={t('vanBanTichHopForm.fXacThucDauVao')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={f.inbound_auth_type}
                  onChange={(v) => set('inbound_auth_type', v as XacThucDauVao)}
                  options={[
                    { value: 'API_KEY', label: t('vanBanTichHopForm.xacThucVao.API_KEY') },
                    { value: 'BEARER', label: t('vanBanTichHopForm.xacThucVao.BEARER') },
                  ]}
                />
              )}
            </Field>
            <Field label={t('vanBanTichHopForm.fDinhDang')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={f.inbound_format}
                  onChange={(v) => set('inbound_format', v as DinhDangDauVao)}
                  options={[
                    { value: 'MULTIPART', label: t('vanBanTichHopForm.dinhDang.MULTIPART') },
                    { value: 'JSON', label: t('vanBanTichHopForm.dinhDang.JSON') },
                  ]}
                />
              )}
            </Field>
          </div>
          <p className="text-xs text-content-muted">{t('vanBanTichHopForm.tiepNhanMo')}</p>
        </Phan>

        <Phan tieuDe={t('vanBanTichHopForm.phanWebhook')}>
          <Field label={t('vanBanTichHopForm.fUrl')} error={loi.webhook_url}>
            {(p) => (
              <Input
                {...p}
                placeholder="https://api.donvi.gov.vn/webhook/foxai-result"
                value={f.webhook_url}
                onChange={(e) => set('webhook_url', e.target.value)}
              />
            )}
          </Field>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
            <Field label={t('vanBanTichHopForm.fPhuongThuc')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={f.webhook_method}
                  onChange={(v) => set('webhook_method', v as PhuongThucWebhook)}
                  options={[
                    { value: 'POST', label: 'POST' },
                    { value: 'PUT', label: 'PUT' },
                  ]}
                />
              )}
            </Field>
            <Field label={t('vanBanTichHopForm.fXacThuc')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={f.webhook_auth_type}
                  onChange={(v) => set('webhook_auth_type', v as XacThucWebhook)}
                  options={(['NONE', 'API_KEY', 'BEARER'] as const).map((v) => ({
                    value: v,
                    label: t(`vanBanTichHopForm.xacThucNgan.${v}`),
                  }))}
                />
              )}
            </Field>
            <Field label={t('vanBanTichHopForm.fThuLai')} error={loi.webhook_retry_max}>
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={0}
                  max={10}
                  value={f.webhook_retry_max}
                  onChange={(e) => set('webhook_retry_max', e.target.value)}
                />
              )}
            </Field>
          </div>
          {f.webhook_auth_type !== 'NONE' && (
            <Field
              label={t(tichHop ? 'vanBanTichHopForm.fTokenSua' : 'vanBanTichHopForm.fToken')}
              error={loi.webhook_token}
            >
              {(p) => (
                <InputPassword
                  {...p}
                  autoComplete="new-password"
                  value={f.webhook_token}
                  onChange={(e) => set('webhook_token', e.target.value)}
                />
              )}
            </Field>
          )}
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-content-primary">{t('vanBanTichHopForm.fSuKien')}</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SU_KIEN_WEBHOOK.map((s) => (
                <Checkbox key={s} checked={f.webhook_triggers.includes(s)} onChange={() => batTat(s)}>
                  <span className="text-sm text-content-secondary">{t(NHAN_SU_KIEN[s])}</span>
                </Checkbox>
              ))}
            </div>
          </fieldset>
        </Phan>

        {tichHop && (
          <Checkbox checked={f.is_active} onChange={(e) => set('is_active', e.target.checked)}>
            <span className="text-sm text-content-secondary">{t('vanBanTichHopForm.fKichHoat')}</span>
          </Checkbox>
        )}
      </div>
    </Dialog>
  );
}
